
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import FrontendLayout from "../../layouts/FrontendLayout";
import Helmet from "../../components/Helmet";
import { ClipLoader } from "react-spinners";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../../api/axiosInstance";
import "react-toastify/dist/ReactToastify.css";
import "./Upload.css";

const DOCUMENTS = [
  {
    label: "Photograph",
    name: "photo",
    icon: "bi-person-bounding-box",
    description: "Upload a recent passport-size photograph.",
    required: true,
  },
  {
    label: "Signature",
    name: "signature",
    icon: "bi-pen",
    description: "Upload a clear image of your signature.",
    required: true,
  },
  {
    label: "Latest Fee Receipt",
    name: "fee_receipt",
    icon: "bi-receipt",
    description: "Proof of fee payment for the current academic session.",
    required: true,
  },
  {
    label: "Family Income Certificate",
    name: "income_certificate",
    icon: "bi-file-earmark-text",
    description: "Certificate must be issued after 31 March 2026.",
    required: true,
  },
  {
    label: "Bank Passbook / Cancelled Cheque",
    name: "bank_passbook",
    icon: "bi-bank",
    description: "Upload the bank passbook page or cheque leaf showing your details.",
    required: true,
  },
  {
    label: "Latest Marksheet",
    name: "final_year_marksheet",
    icon: "bi-mortarboard",
    description: "Upload your latest available marksheet.",
    required: true,
    semesterRestricted: true,
  },
  {
    label: "Undertaking for Employment Status (Employed/Unemployed/Self Employed)",
    name: "unemployment_declaration",
    icon: "bi-file-earmark-check",
    description:
      "Undertaking duly signed by the student regarding status of employment of the student",
    required: true,
  },
  {
    label: "Ration Card",
    name: "ration_card",
    icon: "bi-card-list",
    description: "Upload your ration card, if available.",
    required: false,
  },
];

const getFileType = (url) =>
  /\.pdf(?:$|[?#])/i.test(url || "") ? "pdf" : "image";

export default function Upload() {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const formDataRef = useRef(new FormData());
  const objectUrlsRef = useRef([]);

  const [previews, setPreviews] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [semester, setSemester] = useState("");
  const [final, setFinal] = useState("");
  const [rationCard,setRationCard]=useState("");
  const [uploadedFiles, setUploadedFiles] = useState({});

  useEffect(() => {
    let active = true;

    const fetchUploadedFiles = async () => {
      try {
        const res = await axios.get(`${API_URL}/getUploadedFiles`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!active || !res.data.success || !res.data.data) return;

        const files = res.data.data;

        setSemester(files.SEMESTER);
        setFinal(files.FINAL_SUBMIT);
        setRationCard(files.RATION_CARD_NAME);
        setUploadedFiles(files);

       
        if (files.FORM_STATUS === '0') {
          navigate("/personal");
          return;
        }

        const previewData = {
          photo: files.PHOTO,
          signature: files.SIGNATURE,
          fee_receipt: files.FEE_RECEIPT,
          income_certificate: files.INCOME_CERTIFICATE,
          ration_card: files.RATION_CARD,
          bank_passbook: files.BANK_PASSBOOK,
          final_year_marksheet: files.FINAL_YEAR_MARKSHEET,
          unemployment_declaration: files.UNEMPLOYEMENT_DECLARATION,
        };

        setPreviews(
          Object.fromEntries(
            Object.entries(previewData)
              .filter(([, url]) => Boolean(url))
              .map(([name, url]) => [
                name,
                { type: getFileType(url), url },
              ])
          )
        );
      } catch (err) {
        console.error("Fetch uploaded files failed:", err);
        if (active) toast.error("Unable to load your uploaded documents.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchUploadedFiles();

    return () => {
      active = false;
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [API_URL, navigate]);

  useEffect(() => {
    if (final === 1) {
      navigate("/completed");
    }
  }, [final, navigate]);

  const visibleDocuments = DOCUMENTS.filter(
    (doc) => !(doc.semesterRestricted && semester === "I")
  );

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files?.[0];

    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Only JPG, PNG, or PDF files are allowed.",
      }));
      e.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        [name]: "File size must not exceed 2 MB.",
      }));
      e.target.value = "";
      return;
    }

    setErrors((prev) => ({ ...prev, [name]: null }));

    const previousPreview = previews[name];

    if (
      previousPreview?.url?.startsWith("blob:") &&
      objectUrlsRef.current.includes(previousPreview.url)
    ) {
      URL.revokeObjectURL(previousPreview.url);
      objectUrlsRef.current = objectUrlsRef.current.filter(
        (url) => url !== previousPreview.url
      );
    }

    const previewUrl = URL.createObjectURL(file);
    objectUrlsRef.current.push(previewUrl);

    formDataRef.current.set(name, file);

    setPreviews((prev) => ({
      ...prev,
      [name]: {
        type: file.type === "application/pdf" ? "pdf" : "image",
        url: previewUrl,
        fileName: file.name,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const missing = visibleDocuments.filter(
      (doc) => doc.required && !previews[doc.name]?.url
    );

    if (missing.length > 0) {
      toast.error(
        `Please upload: ${missing.map((doc) => doc.label).join(", ")}`
      );
      return;
    }

    setLoading(true);

    try {
      await api.post("/saveUpload", formDataRef.current);

      toast.success("Documents saved successfully!");

      setTimeout(() => navigate("/fullPreview"), 1200);
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error(err.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const UploadRow = ({ doc, index }) => {
    const preview = previews[doc.name];
    const error = errors[doc.name];

    return (
      <div
        className={`document-card ${preview?.url ? "document-uploaded" : ""} ${error ? "document-error" : ""
          }`}
        style={{ animationDelay: `${index * 50}ms` }}
      >
        <div className="document-info-section">
          <div className="document-meta">
            <div className="document-heading">
              <h6>{doc.label}</h6>

              {doc.required ? (
                <span className="required-badge">Required</span>
              ) : (
                <span className="optional-badge">Optional</span>
              )}
            </div>

            <p className="document-description">{doc.description}</p>
          </div>
        </div>

        <div className="document-upload-section">
          <label className="upload-control">
            <input
              type="file"
              name={doc.name}
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
              disabled={loading}
              aria-label={`Upload ${doc.label}`}
            />
            <span className="upload-control-button">
              <i className="bi bi-cloud-arrow-up me-2" />
              Choose File
            </span>
            <span className="upload-control-hint">
              {preview?.fileName || "JPG, PNG or PDF · Max 2 MB"}
            </span>
          </label>

          {error && (
            <div className="document-validation">
              <i className="bi bi-exclamation-circle me-1" />
              {error}
            </div>
          )}
        </div>

        <div className="document-preview-section">
          {preview?.url ? (
            <div className="file-preview">
              {preview.type === "image" ? (
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="image-preview-link"
                  title="Open image"
                >
                  <img
                    src={preview.url}
                    alt={`${doc.label} preview`}
                  />
                  <span>Preview</span>
                </a>
              ) : (
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pdf-preview-link"
                >
                  <i className="bi bi-file-earmark-pdf" />
                  View PDF
                  <i className="bi bi-box-arrow-up-right ms-1" />
                </a>
              )}

              <span className="file-success">
                <i className="bi bi-check-circle-fill me-1" />
                {preview.fileName ? "Selected" : "Uploaded"}
              </span>
            </div>
          ) : (
            <span className="document-empty-hint">
              <i className="bi bi-dash-circle me-1" />
              No file selected
            </span>
          )}
        </div>

        <div className="document-status">
          {preview?.url ? (
            <i className="bi bi-check-circle-fill" title="Document selected" />
          ) : (
            <i className="bi bi-cloud-arrow-up" title="Awaiting upload" />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet title="Upload Documents | FSS 2026-27" />
      <FrontendLayout />

      <div className="fss-upload-page">
        <ToastContainer position="top-center" autoClose={3000} />

        {loading && (
          <div className="full-page-loader">
            <ClipLoader size={60} />
          </div>
        )}
        <div className="fss-container">
          <h4 className="text-center text-primary fw-bold mb-4">
            FINANCIAL SUPPORT SCHEME (FSS) 2026-27
          </h4>

          <form className="card shadow p-3 p-md-4 mb-4 upload-form" onSubmit={handleSubmit}>
            <h5 className="text-center bg-primary text-white py-2 rounded mb-4">
              Upload Documents
            </h5>

            <div className="document-grid">
              {visibleDocuments.map((doc, index) => (
                <UploadRow key={doc.name} doc={doc} index={index} />
              ))}
            </div>

            <div className="row text-center justify-content-center mt-4 g-0">
              <button
                type="button"
                className="btn btn-warning px-5"
                style={{ width: "50%" }}
                disabled={loading}
                onClick={() => navigate("/preview")}
              >
                Previous
              </button>

              <button
                type="submit"
                className="btn btn-success px-5"
                style={{ width: "50%" }}
                disabled={loading}
              >
                Next
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
